// src/features/map/layers/LiveuamapOverlay.jsx
// Polls /api/layers/liveuamap while the layer is toggled on and the tab is
// visible; pushes GeoJSON into MapView's liveuamap source; renders the
// click popup. Mirrors the RiskmizeMapOverlay mount pattern (and
// AircraftOverlay's skeleton) — stops polling on a 503 (instance has no
// liveuamap key configured), since retrying every minute would be noise.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, X } from 'lucide-react';
import { layers as layersApi } from '@/lib/trackizy';
import useMapLayerPrefs from '@/hooks/useMapLayerPrefs';
import useFeatures from '@/hooks/useFeatures';

const POLL_MS = 60_000;

export default function LiveuamapOverlay({ mapViewRef }) {
  const { t } = useTranslation();
  const { layers: prefs } = useMapLayerPrefs();
  const features = useFeatures();
  const active = prefs.liveuamap && features.liveuamap;
  const [popup, setPopup] = useState(null); // { properties, anchor }

  // Poll + push. On fetch error keep the last pushed data (soft degrade),
  // except a 503 (not_configured) — that means the instance has no key, so
  // stop polling entirely instead of retrying forever.
  useEffect(() => {
    mapViewRef.current?.setLiveuamapVisible?.(active);
    if (!active) {
      setPopup(null);
      return undefined;
    }
    let stopped = false;
    let timer;
    const tick = async () => {
      // `stopped` gates BOTH callers (interval + visibilitychange handler):
      // once the 503 not_configured branch trips it, regaining tab
      // visibility must not resume polling.
      if (stopped) return;
      if (document.visibilityState === 'hidden') return;
      try {
        const fc = await layersApi.liveuamap();
        if (!stopped) mapViewRef.current?.setLiveuamapData?.(fc);
      } catch (err) {
        if (err?.status === 503) {
          stopped = true;
          clearInterval(timer);
          return;
        }
        /* keep last data — never a broken map */
      }
    };
    tick();
    timer = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [active, mapViewRef]);

  // Click → popup (MapView forwards hits as container CustomEvents)
  useEffect(() => {
    const map = mapViewRef.current?.getMap?.();
    const container = map?.getContainer?.();
    if (!container) return undefined;
    const onClick = (e) => setPopup(e.detail || null);
    const onMove = () => setPopup(null);
    container.addEventListener('trackizy:liveuamap-click', onClick);
    map.on('movestart', onMove);
    return () => {
      container.removeEventListener('trackizy:liveuamap-click', onClick);
      map.off('movestart', onMove);
    };
  }, [mapViewRef]);

  if (!popup) return null;
  const p = popup.properties || {};
  // Anchor is in map-CONTAINER pixel coordinates; translate to viewport
  // coordinates via the container's bounding rect so the popup lands under
  // the cursor regardless of where the map sits on the page.
  const rect = mapViewRef.current?.getMap?.()?.getContainer?.()?.getBoundingClientRect?.();
  if (!rect) return null;
  return createPortal(
    <div
      className="fixed z-50 rounded-xl shadow-xl p-3 min-w-[160px] max-w-[240px] text-xs"
      style={{
        left: rect.left + popup.anchor.x + 8, top: rect.top + popup.anchor.y + 8,
        background: 'var(--glass-bg)', backdropFilter: 'blur(12px)',
        border: '1px solid var(--glass-border)',
      }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-semibold text-slate-800 dark:text-slate-100 line-clamp-2"><bdi>{p.title}</bdi></span>
        <button type="button" onClick={() => setPopup(null)} className="ms-auto shrink-0 text-slate-400 hover:text-slate-600"><X size={12} /></button>
      </div>
      <div className="space-y-0.5 text-slate-600 dark:text-slate-300">
        {p.time && <div><bdi>{p.time}</bdi></div>}
        {/^https?:\/\//.test(p.link || '') && (
          <a href={p.link} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-blue-500 hover:underline mt-1">
            <ExternalLink size={11} />{t('mapLayers.openInLiveuamap', 'Open in Liveuamap')}
          </a>
        )}
      </div>
    </div>,
    document.body,
  );
}
